import mem from 'mem';
import {
    DraftCreate,
    DraftCreateMetaSchema,
    NotFoundError,
    PublishedCredentialSchema,
    ResourceCredential,
    ResourceDraft,
} from '../api';
import { UdtClaims } from './credential/claims';
import { DraftCredentialWithClaims } from './credential/draft/with-claims';
import { CredentialFlavor } from './credential/flavor';
import { Claims, UpdatePayloadWithClaims } from './credential/types';
import { VerifiableCredentialWithClaims } from './credential/verifiable/with-claims';
import { getVcSchemaMetadataOrThrow, VcSchema, VcSchemaMetadata } from './decorators';
import { UdtError, UdtErrorCode } from './error';
import { verifiableCredentialDataSchema } from './schemas';
import {
    ApiClient,
    BlobInput,
    CredentialDescriptor,
    Ctor,
    isResourceCredential,
    UpdatePayload,
    UpdatePayloadWithIssueClaims,
    UserUpdateClaims,
} from './types';
import { strictlyConvertToApiType } from './utils/converters';
import { generateKey } from './utils/idempotency';

export type SchemaPublicationResult = {
    published: boolean;
    error?: Error;
};

export type CredentialEnvelope<T extends Claims> = {
    isVerifiable: false;
    credential: DraftCredentialWithClaims<T>;
};

export type VerifiableCredentialEnvelope<T extends Claims> = {
    isVerifiable: true;
    credential: VerifiableCredentialWithClaims<T>;
};

export type LoadedCredential<T extends Claims> = CredentialEnvelope<T> | VerifiableCredentialEnvelope<T>;

export class VcDecorator<T extends Claims> {
    readonly flavor = this.createCredentialFlavor();
    private claims = new UdtClaims(this.client, this.claimsModel);
    private vcSchemaMetadata: VcSchemaMetadata;

    constructor(
        private client: ApiClient,
        private claimsModel: Ctor<T>,
    ) {
        this.vcSchemaMetadata = getVcSchemaMetadataOrThrow(claimsModel);
    }

    protected createCredentialFlavor(): CredentialFlavor {
        return new CredentialFlavor();
    }

    async publishSchema(): Promise<SchemaPublicationResult> {
        if (this.vcSchemaMetadata.slug == null) {
            return {
                published: false,
                error: new UdtError(
                    UdtErrorCode.VcSchemaMissingSlug,
                    `A schema requires a slug to be published as stable`,
                ),
            };
        }

        if (this.vcSchemaMetadata.owner != null && this.vcSchemaMetadata.owner !== (await this.client.tenantId())) {
            return {
                published: false,
                error: new UdtError(
                    UdtErrorCode.VcSchemaDifferentOwner,
                    `It's not possible to publish a schema to a tenant different from the configured one.`,
                ),
            };
        }

        try {
            const credentialSchema = await this.client.schemas.schemaCreate({
                data: {
                    schema: this.claims.getClaimsSchema(),
                },
            });
            await this.client.schemas.schemaPublish(credentialSchema.id, {
                slug: this.vcSchemaMetadata.slug,
                version: this.vcSchemaMetadata.version,
            });
            return { published: true };
        } catch (error) {
            if (error instanceof Error) {
                return { published: false, error };
            }
            throw error;
        }
    }

    @mem.decorator()
    async getCredentialTerm(): Promise<string> {
        let publishedCredentialSchema: PublishedCredentialSchema;
        if (this.vcSchemaMetadata.owner == null) {
            const result = await this.client.schemas.schemaImport({
                data: {
                    schema: this.claims.getClaimsSchema(),
                    version: this.vcSchemaMetadata.version,
                    slug: this.vcSchemaMetadata.slug,
                },
            });
            publishedCredentialSchema = result.data;
        } else if (this.vcSchemaMetadata.slug != null) {
            publishedCredentialSchema = await this.client.desk.publishedMetaSchemaFetch(
                this.vcSchemaMetadata.owner,
                this.vcSchemaMetadata.slug,
                this.vcSchemaMetadata.version,
            );
        } else {
            throw new UdtError(
                UdtErrorCode.VcSchemaMissingSlug,
                'A VcSchema annotation with an owner value requires a slug value',
            );
        }
        if (publishedCredentialSchema.schema.jsonLdTermName != null) {
            return `${publishedCredentialSchema.schema.jsonLdTermName}Credential`;
        }
        return `${publishedCredentialSchema.slug}v${publishedCredentialSchema.version}Credential`;
    }

    async canMap(input: ResourceDraft | ResourceCredential): Promise<boolean> {
        return input.data.type.includes(await this.getCredentialTerm());
    }

    async map(input: ResourceDraft): Promise<DraftCredentialWithClaims<T>>;
    async map(input: ResourceCredential): Promise<VerifiableCredentialWithClaims<T>>;
    async map(
        input: ResourceDraft | ResourceCredential,
    ): Promise<VerifiableCredentialWithClaims<T> | DraftCredentialWithClaims<T>> {
        if (isResourceCredential(input)) {
            // ResourceCredential
            if (!(await this.canMap(input))) {
                throw new UdtError(
                    UdtErrorCode.IncompatibleUDT,
                    `The received ResourceCredential is incompatible with the current VcDecorator instance claims model`,
                );
            }

            return new VerifiableCredentialWithClaims(this.client, this.flavor, this.claimsModel, input);
        } else {
            // ResourceDraft
            if (!(await this.canMap(input))) {
                throw new UdtError(
                    UdtErrorCode.IncompatibleUDT,
                    `The received ResourceDraft is incompatible with the current VcDecorator instance claims model`,
                );
            }

            return new DraftCredentialWithClaims(this.client, this.flavor, this.claimsModel, input.data, input);
        }
    }

    private async importBlob(blob: BlobInput, request?: UpdatePayload): Promise<CredentialDescriptor> {
        const idempotencyKey = generateKey('importBlob');
        const { blobId, uploadUri } = await this.client.credentials.credentialUpload({
            idempotencyKey,
        });

        await fetch(uploadUri, {
            method: 'PUT',
            body: blob,
        });

        const resourceCredential = await this.client.credentials.credentialImport({
            annotations: request?.annotations,
            labels: request?.labels,
            blobId,
            idempotencyKey,
        });

        return {
            data: strictlyConvertToApiType(verifiableCredentialDataSchema, {
                id: resourceCredential.data.id,
                digestMultibase: resourceCredential.data.digestMultibase,
            }),
            id: resourceCredential.id,
            etag: resourceCredential.etag,
            revision: resourceCredential.revision,
        };
    }

    private async convertUDTToDraftCreate(object: UserUpdateClaims<T> | null): Promise<DraftCreate> {
        let metaSchema: DraftCreateMetaSchema;
        let jsonLdVocabs = this.vcSchemaMetadata.jsonLdVocabs;
        let vcJsonSchemas = this.vcSchemaMetadata.vcJsonSchemas;
        if (this.vcSchemaMetadata.owner == null) {
            const claimsSchema = this.claims.getClaimsSchema();
            metaSchema = {
                schema: claimsSchema,
                slug: this.vcSchemaMetadata.slug,
                version: this.vcSchemaMetadata.version,
            };
            if (
                jsonLdVocabs?.includes(VcSchema.jsonLdVocabUrl) ||
                vcJsonSchemas?.includes(VcSchema.vcdmJsonSchemaUrl)
            ) {
                const result = await this.client.schemas.schemaImport({
                    data: {
                        schema: claimsSchema,
                        slug: this.vcSchemaMetadata.slug,
                        version: this.vcSchemaMetadata.version,
                    },
                });
                jsonLdVocabs =
                    jsonLdVocabs == null
                        ? undefined
                        : jsonLdVocabs.map((x) => (VcSchema.jsonLdVocabUrl === x ? result.data.jsonLdVocabUrl : x));
                vcJsonSchemas =
                    vcJsonSchemas == null
                        ? undefined
                        : vcJsonSchemas.map((x) =>
                              VcSchema.vcdmJsonSchemaUrl === x ? result.data.vcdmJsonSchemaUrl : x,
                          );
            }
        } else if (this.vcSchemaMetadata.slug != null) {
            const deskBaseURL = (await this.client.dids.didDocumentSelfGet()).id.split(':')[2];
            metaSchema = `https://${deskBaseURL}/tenants/${this.vcSchemaMetadata.owner}/meta-schema/${this.vcSchemaMetadata.slug}/v${this.vcSchemaMetadata.version}`;
            if (
                jsonLdVocabs?.includes(VcSchema.jsonLdVocabUrl) ||
                vcJsonSchemas?.includes(VcSchema.vcdmJsonSchemaUrl)
            ) {
                const result = await this.client.desk.publishedMetaSchemaFetch(
                    this.vcSchemaMetadata.owner,
                    this.vcSchemaMetadata.slug,
                    this.vcSchemaMetadata.version,
                );
                jsonLdVocabs =
                    jsonLdVocabs == null
                        ? undefined
                        : jsonLdVocabs.map((x) => (VcSchema.jsonLdVocabUrl === x ? result.jsonLdVocabUrl : x));
                vcJsonSchemas =
                    vcJsonSchemas == null
                        ? undefined
                        : vcJsonSchemas.map((x) => (VcSchema.vcdmJsonSchemaUrl === x ? result.vcdmJsonSchemaUrl : x));
            }
        } else {
            throw new UdtError(
                UdtErrorCode.VcSchemaMissingSlug,
                'A VcSchema annotation with an owner value requires a slug value',
            );
        }
        return {
            metaSchema,
            jsonLdVocabs,
            vcdmJsonSchemas: vcJsonSchemas,
            values: object != null ? this.claims.marshalValues(object) : undefined,
        };
    }

    async create(request?: UpdatePayloadWithClaims<T>): Promise<DraftCredentialWithClaims<T>> {
        const { id, etag, revision, data } = await this.client.drafts.draftCreate({
            data: await this.convertUDTToDraftCreate(request?.claims || null),
            annotations: request?.annotations,
            labels: request?.labels,
            idempotencyKey: generateKey('create'),
        });

        return new DraftCredentialWithClaims(this.client, this.flavor, this.claimsModel, data, { id, etag, revision });
    }

    async issue(
        privateKeyId: string,
        request: UpdatePayloadWithIssueClaims<T>,
    ): Promise<VerifiableCredentialWithClaims<T>> {
        const result = await this.create(request);
        return result.issue(privateKeyId);
    }

    async loadAsDraft(id: string, revision?: number): Promise<DraftCredentialWithClaims<T>> {
        const resourceDraft = revision
            ? await this.client.drafts.draftRevision(id, revision)
            : await this.client.drafts.draftLatest(id);

        return this.map(resourceDraft);
    }

    async loadAsCredential(id: string, revision?: number): Promise<VerifiableCredentialWithClaims<T>> {
        const resourceCredential = revision
            ? await this.client.credentials.credentialRevision(id, revision)
            : await this.client.credentials.credentialLatest(id);

        return this.map(resourceCredential);
    }

    async load(id: string, revision?: number): Promise<LoadedCredential<T>> {
        let verifiableCredential: VerifiableCredentialWithClaims<T> | undefined;
        let draftCredential: DraftCredentialWithClaims<T> | undefined;

        try {
            verifiableCredential = await this.loadAsCredential(id, revision);
        } catch (error) {
            if (error instanceof NotFoundError) {
                draftCredential = await this.loadAsDraft(id, revision);
            } else throw error;
        }

        if (!draftCredential) {
            if (!verifiableCredential) throw new Error(`Resource with id '${id}' could not be found.`);

            return {
                isVerifiable: true,
                credential: verifiableCredential,
            };
        } else {
            return {
                isVerifiable: false,
                credential: draftCredential,
            };
        }
    }

    async importCredential(blob: BlobInput, request?: UpdatePayload): Promise<VerifiableCredentialWithClaims<T>> {
        const credential = await this.importBlob(blob, request);

        return new VerifiableCredentialWithClaims(this.client, this.flavor, this.claimsModel, credential);
    }
}
